import React, {
  useState,
  useRef,
  useEffect,
  MouseEvent,
  TouchEvent,
} from "react";
import WHIPClient from "./components/WHIPClient";
import "./style.css";
import { appendNewFiles, handleFilesChange, handleRemoveFile, } from "./utils";
import { Baseline, Highlighter, Upload, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { Post } from "./types/post";
import { useAddPost, useUploadMedias } from "./api/contentApi";
import { useDropzone } from "react-dropzone";
import { WHIP_URL, WORKER_URL } from "./constants";
import { ContentPost } from "./components/contentPost/ContentPost";
import { TextEditor } from "./components/textEditor/TextEditor";
import { SwatchesPicker } from "./components/colorPicker/ColorPicker";
import { Bounce, ToastContainer, toast } from 'react-toastify';
import { Editor } from "@tiptap/react";

export interface FormContent {
  head: string,
  content: string | null,
  mediaKeys: string[] | null,
  mediaFiles: FileList | null
}

const App: React.FC = () => {
  const audioRef = useRef<HTMLAudioElement>(null);

  const [editorInstance, setEditorInstance] = useState<{
    getEditor: () => Editor | null;
  } | null>(null);

  const { mutateAsync: uploadMedias, isPending: isMediasUploading } = useUploadMedias();
  const { mutate: addPost, isPending: isAddPostPending } = useAddPost();

  const [client, setClient] = useState<WHIPClient | null>(null);
  const [status, setStatus] = useState<string>("Press and hold to talk");
  const [posts, setPosts] = useState<Post[]>([]);
  const [editPostId, setEditPostId] = useState<null | number>(null);

  const [textColor, setTextColor] = useState("#000000");
  const [textBg, setTextBg] = useState("#ffffff");

  const [selectTextColor, setSelectTextColor] = useState(false);
  const [selectBgColor, setSelectBgTextColor] = useState(false);

  const createFormAddMediaInput = useRef<HTMLInputElement>(null);

  const createForm = useForm<FormContent>({
    defaultValues: {
      mediaFiles: null,
      content: null
    }
  });

  const createFormData = createForm.watch();

  const { getRootProps: creteFormDndRoot,
    getInputProps: createFormInput,
    isDragActive: isCreateDragActive } = useDropzone({
      accept: {
        'image/*': [],
        'video/*': [],
        'audio/*': []
      },
      onDrop: (acceptedFiles: File[]) => {
        const existingFiles = Array.from(createForm.getValues("mediaFiles") ?? []);
        appendNewFiles(createForm, existingFiles, acceptedFiles);
      }
    });


  useEffect(() => {
    let whip: WHIPClient | null = null;

    if (audioRef.current) {
      whip = new WHIPClient(WHIP_URL, audioRef.current);
      setClient(whip);
    }
  }, []);

  const toggleMic = (on: boolean) => {
    if (client) client.enableMic(on);
    setStatus(on ? "Release to stop talking" : "Press and hold to talk");
  };

  const handleMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    toggleMic(true);
  };
  const handleMouseUp = (e: MouseEvent) => {
    e.preventDefault();
    toggleMic(false);
  };
  const handleTouchStart = (e: TouchEvent) => {
    e.preventDefault();
    toggleMic(true);
  };
  const handleTouchEnd = (e: TouchEvent) => {
    e.preventDefault();
    toggleMic(false);
  };

  const handleSubmit = async (data: FormContent) => {
    const { mediaFiles, head } = data;
    let mediaKeys: string[] = [];

    try {
      const editor = editorInstance?.getEditor();
      if (editor) {
        if (mediaFiles) await uploadMedias(mediaFiles, {
          onSuccess: (keys) => {
            mediaKeys = keys
          }
        });
        const newPost = { content: editor.getHTML(), head, mediaKeys, headerBg: textBg, headerTextColor: textColor };
        addPost(newPost, {
          onSuccess: ((newPost) => {
            setPosts([newPost, ...posts]);
            createForm.reset();
            editor.commands.clearContent();

            setTextColor("#000000");
            setTextBg("#ffffff");

            toast("Post added!", {
              type: "success"
            });
          })
        });

      }
    } catch (err) {
      toast("Create post error!", {
        type: "error"
      });
      console.error(err);
    }
  };

  const fetchPosts = async () => {
    try {
      const res = await fetch(`${WORKER_URL}/posts/getLatest?date=${Date.now()}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = await res.json();
      setPosts(data);
    } catch (err) {
      toast("Get latest posts error", {
        type: "error"
      });
      console.error(err);
    }
  };


  useEffect(() => {
    fetchPosts();
    const interval = setInterval(() => {
      if (!document.hidden) {
        fetch(`${WORKER_URL}/broadcasting/update`, { method: "POST" });
      }
    }, 2000);
    return () => {
      clearInterval(interval);
    };
  }, []);
  useEffect(() => {
    if (!editPostId) {
      const handlePaste = (e: ClipboardEvent) => {
        if (!e.clipboardData) return;
        const clipboardFiles = Array.from(e.clipboardData.files).filter((file) =>
          file.type.startsWith('image/') || file.type.startsWith('video/')
        );

        if (clipboardFiles.length) {
          const existingFiles = Array.from(createForm.getValues("mediaFiles") ?? []);
          appendNewFiles(createForm, existingFiles, clipboardFiles);
        }
      };

      window.addEventListener('paste', handlePaste);
      return () => window.removeEventListener('paste', handlePaste);
    }
  }, [editPostId]);

  return (
    <div className="container">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick={false}
        theme="light"
        transition={Bounce}
      />
      <div className="content">
        <button
          id="push-to-talk"
          className={"talk-button"}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          Push to talk
        </button>
        <div className="status" id="status">
          {status}
        </div>
        <audio id="input-audio" ref={audioRef} controls muted />
        <form className="form" id="post-form" onSubmit={createForm.handleSubmit(handleSubmit)}>
          <div className="form-input-row">
            <input
              className="form-input"
              type="text"
              placeholder="Title…"
              {...createForm.register("head", { required: true })}
              style={{
                backgroundColor: textBg, color: textColor
              }}
              required
              autoComplete="off"
            />
            <button
              className={`toolbar__color-button header-color-select ${selectTextColor ? "selected-button" : ""}`}
              onClick={() => {
                setSelectTextColor(true);
                setSelectBgTextColor(false);
              }}
              type="button">
              <Baseline width={18} height={18} />
              <div className="toolbar__current-color" style={{ backgroundColor: `${textColor}` }} />
            </button>
            <button
              className={`toolbar__color-button header-color-select ${selectBgColor ? "selected-button" : ""}`}
              onClick={() => {
                setSelectBgTextColor(true);
                setSelectTextColor(false);
              }}
              type="button">
              <Highlighter width={18} height={18} />
              <div className="toolbar__current-color" style={{ backgroundColor: `${textBg}` }} />
            </button>
            {selectBgColor && <div className="color-picker-container">
              <SwatchesPicker
                color={textBg}
                onChange={setTextBg}
                onClose={() => {
                  setSelectBgTextColor(false);
                }}
                setDefault={() => setTextBg("#ffffff")}
                textColor={textColor}
                bgColor={textBg}
              />
            </div>
            }
            {selectTextColor && <div className="color-picker-container">
              <SwatchesPicker
                color={textColor}
                onChange={setTextColor}
                onClose={() => {
                  setSelectTextColor(false);
                }}
                setDefault={() => setTextColor("#000000")}
                textColor={textColor}
                bgColor={textBg} />
            </div>
            }
            <button className="form-button" type="submit" disabled={isAddPostPending || isMediasUploading}>
              Send
            </button>
          </div>
          <div className="form-inputs">
            <TextEditor ref={setEditorInstance} />
            <div className="add-media-area" {...creteFormDndRoot()}>
              <div
                className={`media-dnd-area ${isCreateDragActive ? "drag-active" : ""}`}
                onClick={() => {
                  createFormAddMediaInput.current?.click()
                }}>
                <div className="media-dnd-area-text">
                  <span>Upload medias from computer or drag and drop</span>
                </div>
                <Upload />
              </div>
            </div>
            <input
              className="form-media-input"
              type="file"
              accept="image/*,video/*"
              multiple
              {...createFormInput()}
              {...createForm.register("mediaFiles")}
              ref={(e) => {
                createForm.register("mediaFiles").ref(e);
                createFormAddMediaInput.current = e;
              }}
              onChange={(e) => handleFilesChange(e, createForm)}
            />
            {createFormData.mediaFiles && <div className="preview-container">
              {Array.from(createFormData.mediaFiles).map((media, i) => {
                return <div className="edit-image-container" key={i}>
                  <button
                    type="button"
                    className="remove-image"
                    onClick={() => handleRemoveFile(media, createForm)}>
                    <X />
                  </button>
                  <img className="preview" src={URL.createObjectURL(media)} />
                </div>
              })}
            </div>}
          </div>
        </form>
        <div>
          <h1 className="posts-label">Posts:</h1>
          <div>
            {posts.map((post, i) => (
              <div key={i}>
                <ContentPost
                  post={post}
                  posts={posts}
                  editPostId={editPostId}
                  setEditPostId={setEditPostId}
                  setPosts={setPosts} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;
